import { logEvent } from "@shared/analytics/analytics";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFailuresList, useIgnoreFailure } from "../../hooks";
import FailureDashboard from "./dashboard/FailureDashboard";
import { FailureFilters, FailureFiltersComponent } from "./filters/filters";

// Updated interface to match API response with camelCase
interface Failure {
  id: number;
  failureNo: string | null;
  failureAriseDate: string;
  priority: number;
  priorityDisplay: string;
  status: number;
  statusDisplay: string;
  asset: {
    id: number;
    assetNo: string;
    assetName: string;
    location: string | null;
    status: number;
    statusDisplay: string;
    description: string | null;
    createdDate: string;
    createdUserRemoteName: string;
    lastModifiedUserRemoteName: string;
  };
  description: string;
  createdDate: string;
  createdUserRemoteName: string;
  lastModifiedUserRemoteName: string;
}

interface ApiResponse {
  pageNumber: number;
  count: number;
  next: string | null;
  previous: string | null;
  results: Failure[];
}

type FailureApiFilters = {
  page: number;
  limit: number;
  search?: string;
  status?: string;
  priority?: string;
  assetType?: string;
  searchData?: string;
};

const Landing = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const ignoreFailureMutation = useIgnoreFailure();
  const [isIgnoreDialogOpen, setIsIgnoreDialogOpen] = useState(false);
  const [failureToIgnore, setFailureToIgnore] = useState<Failure | null>(null);

  // Get URL parameters similar to incident pattern
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      page: parseInt(searchParams.get("page") || "1", 10),
      search: searchParams.get("query") || "",
      status: searchParams.get("status") || "",
      priority: searchParams.get("priority") || "",
      assetType: searchParams.get("assetType") || "",
      date_from: searchParams.get("date_from") || "",
      date_to: searchParams.get("date_to") || "",
    };
  }, [location.search]);

  const [currentPage, setCurrentPage] = useState(urlParams.page);
  const [filters, setFilters] = useState<FailureFilters>({
    status: urlParams.status,
    priority: urlParams.priority,
    assetType: urlParams.assetType,
    date_from: urlParams.date_from,
    date_to: urlParams.date_to,
    search: urlParams.search,
  });

  // Convert filters to API format
  const apiParams = useMemo(() => {
    const params: FailureApiFilters = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    if (filters.search?.trim()) {
      params.searchData = filters.search;
      params.search = filters.search;
    }
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.assetType) params.assetType = filters.assetType;

    return {
      remote_utility_id: remoteUtilityId,
      ...params,
    };
  }, [filters, currentPage, remoteUtilityId]);

  // Use API hook with filters
  const { data: failuresResponse, refetch } = useFailuresList(apiParams);

  // Update filters when URL changes
  useEffect(() => {
    setCurrentPage(urlParams.page);
    setFilters({
      status: urlParams.status,
      priority: urlParams.priority,
      assetType: urlParams.assetType,
      date_from: urlParams.date_from,
      date_to: urlParams.date_to,
      search: urlParams.search,
    });
  }, [urlParams]);

  // Extract data from API response
  const failures = failuresResponse?.results || [];
  const totalItems = failuresResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  // Update URL helper function
  const updateUrl = (
    updates: Partial<{
      page: number;
      query: string;
      status: string;
      priority: string;
      assetType: string;
      date_from: string;
      date_to: string;
    }>
  ) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value !== "" && value !== 0) {
          searchParams.set(key, String(value));
        } else {
          searchParams.delete(key);
        }
      } else {
        searchParams.delete(key);
      }
    });
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSearch = (searchValue: string) => {
    setFilters({
      ...filters,
      search: searchValue,
    });
    updateUrl({ query: searchValue, page: 1 });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrl({ page });
  };

  const handleFiltersChange = (newFilters: FailureFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleFiltersSubmit = (formData: FailureFilters) => {
    updateUrl({
      status: formData.status,
      priority: formData.priority,
      assetType: formData.assetType,
      date_from: formData.date_from,
      date_to: formData.date_to,
      query: formData.search,
      page: 1,
    });
  };

  const handleFiltersReset = () => {
    const defaultFilters: FailureFilters = {
      status: "",
      priority: "",
      assetType: "",
      date_from: "",
      date_to: "",
      search: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Utility functions for styling based on API response values
  const getPriorityColor = (priorityDisplay: string) => {
    const priority = priorityDisplay.toLowerCase();
    if (priority.includes("critical") || priority.includes("high")) {
      return "bg-red-100 text-red-800 border-red-200";
    } else if (priority.includes("medium")) {
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (priority.includes("low")) {
      return "bg-green-100 text-green-800 border-green-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getStatusColor = (statusDisplay: string) => {
    const status = statusDisplay.toLowerCase();
    if (status.includes("new")) {
      return "bg-blue-100 text-blue-800 border-blue-200";
    } else if (status.includes("progress")) {
      return "bg-purple-100 text-purple-800 border-purple-200";
    } else if (status.includes("pending")) {
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (status.includes("resolved") || status.includes("completed")) {
      return "bg-green-100 text-green-800 border-green-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  // Action handlers
  const handleViewFailureDetails = (failure: Failure) => {
    logEvent("View Failure Details", {
      failureId: failure.id,
      failureNo: failure.failureNo,
      page: "Failures Landing",
      action: "view_details",
    });
    navigate(`/ax/field-alerts/failure/${failure.id}`);
  };

  const handleEditFailure = (failure: Failure) => {
    logEvent("Edit Failure", {
      failureId: failure.id,
      failureNo: failure.failureNo,
      page: "Failures Landing",
      action: "edit_failure",
    });
    navigate(`/ax/field-alerts/failure/report/edit/${failure.id}`);
  };

  const handleIgnoreFailureClick = (failure: Failure) => {
    if (failure.statusDisplay.toLowerCase().includes("ignored")) {
      toast({
        title: "Error",
        description: "Failure is already ignored",
        variant: "destructive",
      });
      return;
    }
    setFailureToIgnore(failure);
    setIsIgnoreDialogOpen(true);
  };

  const handleConfirmIgnoreFailure = async () => {
    if (!failureToIgnore) return;

    try {
      logEvent("Ignore Failure", {
        failureId: failureToIgnore.id,
        failureNo: failureToIgnore.failureNo,
        page: "Failures Landing",
        action: "ignore_failure",
      });

      ignoreFailureMutation.mutate(
        {
          id: failureToIgnore.id.toString(),
          payload: {
            status: 6,
            reject_remark: "ignored",
          },
        },
        {
          onSuccess: (data, variables) => {
            toast({
              title: "Success",
              description: "Failure ignored successfully",
              variant: "default",
            });
            queryClient.refetchQueries({
              queryKey: QueryKeyFactory.module.ax.failures
                .failuresList({
                  remote_utility_id: remoteUtilityId,
                })
                .slice(0, 2),
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to ignore failure",
        variant: "destructive",
      });
    } finally {
      setIsIgnoreDialogOpen(false);
      setFailureToIgnore(null);
    }
  };

  const handleCloseIgnoreDialog = () => {
    setIsIgnoreDialogOpen(false);
    setFailureToIgnore(null);
  };

  const handleExport = () => {
    // TODO: Implement actual export logic
  };

  // Table actions
  const tableActions = [
    {
      label: "View Details",
      onClick: (row: Failure) => handleViewFailureDetails(row),
      icon: "Eye" as const,
      variant: "outline" as const,
    },
    {
      label: "Edit Failure",
      onClick: (row: Failure) => handleEditFailure(row),
      icon: "Pencil" as const,
      variant: "outline" as const,
    },
    {
      label: "Ignore Failure",
      onClick: (row: Failure) => handleIgnoreFailureClick(row),
      icon: "X" as const,
      disabled: (row: Failure) =>
        row.statusDisplay.toLowerCase().includes("draft") ||
        row.statusDisplay.toLowerCase().includes("ignored"),
      variant: "outline" as const,
    },
  ];

  // Define table columns based on API response structure
  const failureColumns: TableColumn<Failure>[] = useMemo(
    () => [
      {
        key: "failureNo",
        header: "Failure ID",
        type: "custom",
        headerTooltip: "A unique system-generated identifier.",
        render: (value: string) => <div className="ml-6">{value}</div>,
      },
      {
        key: "asset",
        header: "Asset ID / Name",
        type: "custom",
        headerTooltip:
          "Asset identifier and descriptive name. Asset ID is used for system tracking while the name provides human-readable context for quick identification.",
        render: (asset: Failure["asset"]) => (
          <div>
            <div className="font-medium ml-6">{asset.assetNo}</div>
            <div className="text-sm text-gray-500 ml-6">{asset.assetName}</div>
          </div>
        ),
      },
      {
        key: "asset",
        header: "Location",
        type: "custom",
        headerTooltip:
          "Physical location or address where the asset is installed. Used for dispatching field teams and identifying geographic patterns in failures.",
        render: (asset: Failure["asset"]) => {
          const value = asset?.location;
          return <HoverRevealText text={value} />;
        },
      },
      {
        key: "description",
        header: "Description",
        type: "custom",
        headerTooltip:
          "Brief description of the failure symptoms or issue provided during reporting. Detailed investigation notes and resolution details are stored within the failure record.",
        render: (value: string) => <HoverRevealText text={value} />,
      },
      {
        key: "priorityDisplay",
        header: "Priority",
        type: "badge",
        headerTooltip: (
          <ul>
            <li>Severity level of the failure:</li>
            <li>
              <strong>Critical:</strong> Immediate response required
              (service-affecting).
            </li>
            <li>
              <strong>High:</strong> Same-day response needed (potential service
              impact).
            </li>
            <li>
              <strong>Medium:</strong> Response within 24-48 hours (quality
              issue).
            </li>
            <li>
              <strong>Low:</strong> Non-urgent, scheduled maintenance window.
            </li>
            <li>
              Priority impacts assignment, SLA tracking, and escalation
              workflows.
            </li>
          </ul>
        ),
        badgeConfig: {
          getClassName: (value: string) => getPriorityColor(value),
        },
      },
      {
        key: "statusDisplay",
        header: "Status",
        type: "badge",
        headerTooltip: (
          <ul>
            <li>Shows the current stage of the failure resolution workflow:</li>
            <li>
              <strong>New:</strong> Just reported, awaiting assignment.
            </li>
            <li>
              <strong>In Progress:</strong> Field team assigned and
              investigating.
            </li>
            <li>
              <strong>Pending:</strong> Waiting for parts, approval, or external
              dependency.
            </li>
            <li>
              <strong>Resolved:</strong> Fixed and verified, closed with
              resolution notes.
            </li>
            <li>
              <strong>Ignored:</strong> Dismissed with documented reason.
            </li>
            <li>Status badges are color-coded for quick visual scanning.</li>
          </ul>
        ),
        badgeConfig: {
          getClassName: (value: string) => getStatusColor(value),
        },
      },
      {
        key: "createdUserRemoteName",
        header: "Reported By",
        type: "custom",
        headerTooltip:
          "Person or system that logged the failure. Can be a technician, automated monitoring system, or customer report. Used for contact and audit purposes.",
        render: (value: string) => <div className="ml-6">{value || "N/A"}</div>,
      },
      {
        key: "createdDate",
        header: "Date Reported",
        type: "custom",
        headerTooltip:
          "The exact date and time the failure was recorded in the system. Time is logged in the utility's standard time zone and used for SLA tracking.",
        render: (value: string) => <div className="ml-6">{value || "N/A"}</div>,
      },
    ],
    []
  );

  const startItem = totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <PageHeader
            title="Failures"
            subtitle="Track and manage reported asset failures"
          />
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              logEvent("Report New Failure", {
                page: "Failures Landing",
                action: "create_failure",
              });
              navigate("/ax/field-alerts/failure/report/create");
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Report New Failure
          </Button>
        </div>
      </div>

      {/* Dashboard Metrics */}
      <FailureDashboard failures={failures} />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isIgnoreDialogOpen}
        onClose={handleCloseIgnoreDialog}
        onConfirm={handleConfirmIgnoreFailure}
        title="Ignore Failure"
        itemName={
          failureToIgnore?.failureNo || `Failure #${failureToIgnore?.id}`
        }
        description={`Are you sure you want to ignore "${
          failureToIgnore?.failureNo || `Failure #${failureToIgnore?.id}`
        }"? This will mark the failure as ignored and remove it from active tracking.`}
        confirmText="Ignore Failure"
        isLoading={ignoreFailureMutation.isPending}
      />

      {/* Failures Table */}
      <div className="mt-8">
        <GenericTable
          data={failures}
          columns={failureColumns}
          rowKey="id"
          searchPlaceholder="Search failures by ID or reporter..."
          searchParamName="query"
          searchDebounceMs={300}
          searchValue={filters.search}
          onSearch={handleSearch}
          actions={tableActions}
          actionsType="dropdown"
          pagination={{
            currentPage: currentPage,
            totalPages: totalPages,
            totalItems: totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
          filterPanel={
            <FailureFiltersComponent
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersSubmit}
              onClearFilters={handleFiltersReset}
            />
          }
          onExport={handleExport}
          exportLoading={false}
          emptyMessage="No failures found matching your criteria"
          getRowClassName={(row) => "hover:bg-gray-50"}
        />
      </div>
    </div>
  );
};

export default Landing;
